import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import PageHeader from '@/components/Layout/PageHeader';
import { salesCreditsAPI } from '@/lib/api';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import SearchableSelect from '@/components/SearchableSelect';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ShoppingCart,
  CreditCard,
  ArrowClockwise,
  MagnifyingGlass,
  CaretLeft,
  CaretRight,
  Receipt,
  TrendUp,
  TrendDown,
  Equals,
  Funnel,
  CheckCircle,
  Clock,
  Prohibit,
  ArrowRight,
  UploadSimple,
} from '@phosphor-icons/react';
import CreditNoteDetailDialog from '@/components/CreditNoteDetailDialog';
import CustomerDrillDownDialog from '@/components/CustomerDrillDownDialog';

const FY_OPTIONS = [
  { value: '2026', label: 'FY 2026-27' },
  { value: '2025', label: 'FY 2025-26' },
  { value: '2024', label: 'FY 2024-25' },
  { value: '2023', label: 'FY 2023-24' },
];

function StatusBadge({ status, type }) {
  const colors = {
    closed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    open: 'bg-blue-50 text-blue-700 border-blue-200',
    void: 'bg-gray-100 text-gray-500 border-gray-200',
    invoiced: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    partially_invoiced: 'bg-amber-50 text-amber-700 border-amber-200',
    not_invoiced: 'bg-red-50 text-red-600 border-red-200',
    overdue: 'bg-red-50 text-red-700 border-red-200',
    draft: 'bg-gray-100 text-gray-500 border-gray-200',
    paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    partially_paid: 'bg-amber-50 text-amber-700 border-amber-200',
    unpaid: 'bg-red-50 text-red-600 border-red-200',
  };
  const label = type === 'order_status'
    ? (status === 'closed' ? 'Confirmed' : status === 'open' ? 'Pending' : status)
    : (status || '').replace(/_/g, ' ');
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${colors[status] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
      {label}
    </span>
  );
}

export default function SalesCreditsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'reconciliation');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [releasingSerials, setReleasingSerials] = useState(false);

  const handleReleaseCNSerials = async () => {
    if (!window.confirm('Return all credit-note serial numbers back to available stock so they can be invoiced again?')) return;
    setReleasingSerials(true);
    try {
      const { data } = await salesCreditsAPI.releaseCNSerials();
      toast.success(`${data.serials_released} serial(s) returned to stock from ${data.credit_notes_with_serials} credit notes`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to release credit-note serials');
    } finally { setReleasingSerials(false); }
  };

  // Reconciliation
  const [recon, setRecon] = useState(null);
  const [selectedFY, setSelectedFY] = useState('2026');

  // Sales Orders
  const [salesOrders, setSalesOrders] = useState([]);
  const [soTotal, setSoTotal] = useState(0);
  const [soPage, setSoPage] = useState(1);
  const [soTotalPages, setSoTotalPages] = useState(1);
  const [soSummary, setSoSummary] = useState({});
  const [soConfirmed, setSoConfirmed] = useState({});
  const [soPending, setSoPending] = useState({});
  const [soOrderStatusBd, setSoOrderStatusBd] = useState({});
  const [soSearch, setSoSearch] = useState('');
  const [soOrderStatus, setSoOrderStatus] = useState('all');
  const [soStartDate, setSoStartDate] = useState('');
  const [soEndDate, setSoEndDate] = useState('');
  const [soLocation, setSoLocation] = useState('all');
  const [soSalesperson, setSoSalesperson] = useState('all');
  const [soLocations, setSoLocations] = useState([]);
  const [soSalespersons, setSoSalespersons] = useState([]);
  const [soSortBy, setSoSortBy] = useState('date');
  const [soSortDir, setSoSortDir] = useState('desc');
  const [selectedSO, setSelectedSO] = useState(null);
  const [selectedSODetail, setSelectedSODetail] = useState(null);

  // Whenever an SO is selected, fetch its enriched detail (with linked invoices+serials)
  useEffect(() => {
    if (!selectedSO?.id) { setSelectedSODetail(null); return; }
    let cancelled = false;
    api.get(`/billing/admin/sales-orders/${selectedSO.id}`).then(({ data }) => {
      if (!cancelled) setSelectedSODetail(data);
    }).catch(() => { if (!cancelled) setSelectedSODetail(null); });
    return () => { cancelled = true; };
  }, [selectedSO]);

  // Credit Notes
  const [creditNotes, setCreditNotes] = useState([]);
  const [cnTotal, setCnTotal] = useState(0);
  const [cnPage, setCnPage] = useState(1);
  const [cnTotalPages, setCnTotalPages] = useState(1);
  const [cnSummary, setCnSummary] = useState({});
  const [cnStatusBd, setCnStatusBd] = useState({});
  const [cnSearch, setCnSearch] = useState('');
  const [cnStatus, setCnStatus] = useState('all');
  const [cnStartDate, setCnStartDate] = useState(searchParams.get('start_date') || '');
  const [cnEndDate, setCnEndDate] = useState(searchParams.get('end_date') || '');
  const [selectedCN, setSelectedCN] = useState(null);
  const [drill, setDrill] = useState(null);

  const switchTab = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const loadRecon = useCallback(async () => {
    try {
      const res = await salesCreditsAPI.getReconciliation(selectedFY);
      setRecon(res.data);
    } catch (e) { console.error('Recon error:', e); }
  }, [selectedFY]);

  const loadSalesOrders = useCallback(async () => {
    try {
      const params = { page: soPage, page_size: 25 };
      if (soSearch) params.search = soSearch;
      if (soOrderStatus !== 'all') params.order_status = soOrderStatus;
      if (soStartDate) params.date_start = soStartDate;
      if (soEndDate) params.date_end = soEndDate;
      if (soLocation !== 'all') params.location = soLocation;
      if (soSalesperson !== 'all') params.salesperson = soSalesperson;
      if (soSortBy) params.sort_by = soSortBy;
      if (soSortDir) params.sort_dir = soSortDir;
      const res = await salesCreditsAPI.getSalesOrders(params);
      setSalesOrders(res.data.orders || []);
      setSoTotal(res.data.total || 0);
      setSoTotalPages(res.data.total_pages || 1);
      setSoSummary(res.data.summary || {});
      setSoConfirmed(res.data.confirmed || {});
      setSoPending(res.data.pending || {});
      setSoOrderStatusBd(res.data.order_status_breakdown || {});
      setSoLocations(res.data.locations || []);
      setSoSalespersons(res.data.salespersons || []);
    } catch (e) { console.error('SO error:', e); }
  }, [soPage, soSearch, soOrderStatus, soStartDate, soEndDate, soLocation, soSalesperson, soSortBy, soSortDir]);

  const [convertingSO, setConvertingSO] = useState(null);
  const handleConvertSO = async (so) => {
    if (so.converted_to_invoice_id) { toast.info('Already converted'); return; }
    if (!window.confirm(`Convert ${so.so_number || so.salesorder_number} to an invoice?`)) return;
    setConvertingSO(so.id);
    try {
      const { data } = await salesCreditsAPI.convertSOToInvoice(so.id);
      toast.success(`Converted to ${data.invoice_number}`);
      setSelectedSO(null);
      loadSalesOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Conversion failed');
    } finally { setConvertingSO(null); }
  };


  const loadCreditNotes = useCallback(async () => {
    try {
      const params = { page: cnPage, page_size: 25 };
      if (cnSearch) params.search = cnSearch;
      if (cnStatus !== 'all') params.status = cnStatus;
      if (cnStartDate) params.date_start = cnStartDate;
      if (cnEndDate) params.date_end = cnEndDate;
      const res = await salesCreditsAPI.getCreditNotes(params);
      setCreditNotes(res.data.notes || []);
      setCnTotal(res.data.total || 0);
      setCnTotalPages(res.data.total_pages || 1);
      setCnSummary(res.data.summary || {});
      setCnStatusBd(res.data.status_breakdown || {});
    } catch (e) { console.error('CN error:', e); }
  }, [cnPage, cnSearch, cnStatus, cnStartDate, cnEndDate]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadRecon(), loadSalesOrders(), loadCreditNotes()])
      .finally(() => setLoading(false));
  }, [loadRecon, loadSalesOrders, loadCreditNotes]);

  const refreshAll = () => {
    setLoading(true);
    Promise.all([loadRecon(), loadSalesOrders(), loadCreditNotes()])
      .finally(() => setLoading(false));
  };

  const handleSOExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await salesCreditsAPI.uploadSalesOrdersExcel(file);
      const { toast } = await import('sonner');
      toast.success(`Uploaded! ${data.imported} sales orders imported.`);
      loadSalesOrders();
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); e.target.value = ''; }
  };

  const tabs = [
    { key: 'reconciliation', label: 'Revenue Reconciliation', icon: TrendUp },
    { key: 'sales-orders', label: `Sales Orders (${soTotal})`, icon: ShoppingCart },
    { key: 'credit-notes', label: `Credit Notes (${cnTotal})`, icon: CreditCard },
  ];

  return (
    <div data-testid="sales-credits-page">
      <PageHeader
        title="Sales Orders & Credit Notes"
        subtitle="Confirmed advance payments, credit adjustments & net revenue reconciliation"
        actions={
          <div className="flex items-center gap-2">
            <label className="cursor-pointer">
              <input type="file" accept=".xlsx,.xls" onChange={handleSOExcelUpload} className="hidden" data-testid="so-bulk-upload-input" />
              <Button variant="outline" size="sm" asChild disabled={uploading}>
                <span><UploadSimple size={14} className="mr-1" /> {uploading ? 'Uploading...' : 'Upload SOs Excel'}</span>
              </Button>
            </label>
            <Button variant="outline" size="sm" onClick={handleReleaseCNSerials} disabled={releasingSerials} data-testid="release-cn-serials-btn" title="Return credit-note serials to available stock">
              <ArrowClockwise size={14} className="mr-1" /> {releasingSerials ? 'Releasing...' : 'Release CN Serials'}
            </Button>
            <Button variant="outline" onClick={refreshAll} data-testid="refresh-sales-credits-btn">
              <ArrowClockwise size={16} className="mr-2" />Refresh
            </Button>
          </div>
        }
      />

      <div className="page-content">
        {/* Tab Navigation */}
        <div className="flex items-center gap-1 bg-white border border-[#E2E8F0] rounded-lg p-1 mb-6" data-testid="sales-credits-tabs">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => switchTab(key)}
              data-testid={`tab-${key}`}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === key
                  ? 'bg-[#0F4C81] text-white'
                  : 'text-[#64748B] hover:text-[#0F172A] hover:bg-[#F8FAFC]'
              }`}
            >
              <Icon size={16} />{label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white border border-[#E2E8F0] rounded-lg p-6">
                <div className="skeleton h-6 w-48 mb-4 rounded" />
                <div className="skeleton h-4 w-full rounded" />
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* ═══ RECONCILIATION TAB ═══ */}
            {activeTab === 'reconciliation' && recon && (
              <div className="space-y-6">
                {/* FY Selector */}
                <div className="flex items-center gap-3">
                  <Select value={selectedFY} onValueChange={(v) => { setSelectedFY(v); }}>
                    <SelectTrigger className="w-44" data-testid="recon-fy-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-[#64748B]">{recon.financial_year}</span>
                </div>

                {/* Revenue Formula Cards — CLICKABLE */}
                <div className="bg-white border border-[#E2E8F0] rounded-lg p-6" data-testid="revenue-formula">
                  <h3 className="text-lg font-semibold font-['Plus_Jakarta_Sans'] text-[#0F172A] mb-1">Net Revenue Formula</h3>
                  <p className="text-xs text-[#64748B] mb-5">Only Confirmed (closed) Sales Orders and Closed Credit Notes are counted</p>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex-1 min-w-[180px] bg-[#F8FAFC] rounded-lg p-4 text-center">
                      <Receipt size={24} className="mx-auto text-[#0F4C81] mb-2" />
                      <p className="text-xs text-[#64748B] mb-1">Invoices</p>
                      <p className="text-xl font-bold text-[#0F172A]" data-testid="recon-invoices-total">{formatCurrency(recon.invoices?.total || 0)}</p>
                      <p className="text-xs text-[#64748B]">{(recon.invoices?.count || 0).toLocaleString()} records</p>
                    </div>
                    <TrendUp size={24} className="text-emerald-600 flex-shrink-0" />
                    <div
                      className="flex-1 min-w-[180px] bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center cursor-pointer hover:border-emerald-400 transition-colors"
                      onClick={() => switchTab('sales-orders')}
                      data-testid="recon-so-card-clickable"
                    >
                      <CheckCircle size={24} className="mx-auto text-emerald-600 mb-2" />
                      <p className="text-xs text-emerald-600 mb-1">Confirmed Sales Orders</p>
                      <p className="text-xl font-bold text-emerald-700" data-testid="recon-so-total">{formatCurrency(recon.sales_orders?.total || 0)}</p>
                      <p className="text-xs text-emerald-600">{(recon.sales_orders?.count || 0).toLocaleString()} confirmed</p>
                    </div>
                    <TrendDown size={24} className="text-red-500 flex-shrink-0" />
                    <div
                      className="flex-1 min-w-[180px] bg-red-50 border border-red-200 rounded-lg p-4 text-center cursor-pointer hover:border-red-400 transition-colors"
                      onClick={() => switchTab('credit-notes')}
                      data-testid="recon-cn-card-clickable"
                    >
                      <CreditCard size={24} className="mx-auto text-red-500 mb-2" />
                      <p className="text-xs text-red-500 mb-1">Credit Notes</p>
                      <p className="text-xl font-bold text-red-600" data-testid="recon-cn-total">{formatCurrency(recon.credit_notes?.total || 0)}</p>
                      <p className="text-xs text-red-500">{(recon.credit_notes?.count || 0).toLocaleString()} closed</p>
                    </div>
                    <Equals size={24} className="text-[#0F4C81] flex-shrink-0" />
                    <div className="flex-1 min-w-[180px] bg-[#0F4C81] rounded-lg p-4 text-center">
                      <p className="text-xs text-emerald-300 mb-1">Net Revenue</p>
                      <p className="text-2xl font-bold text-white" data-testid="recon-net-revenue">{formatCurrency(recon.net_revenue || 0)}</p>
                      <p className="text-xs text-emerald-300">Collection Rate: {recon.collection_rate}%</p>
                    </div>
                  </div>
                </div>

                {/* Collections Card */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white border border-[#E2E8F0] rounded-lg p-5">
                    <p className="text-xs text-[#64748B] mb-1">Total Collections</p>
                    <p className="text-2xl font-bold text-[#0F172A]" data-testid="recon-collections">{formatCurrency(recon.collections?.total || 0)}</p>
                    <p className="text-xs text-[#64748B]">{(recon.collections?.count || 0).toLocaleString()} payments</p>
                  </div>
                  <div className="bg-white border border-[#E2E8F0] rounded-lg p-5">
                    <p className="text-xs text-[#64748B] mb-1">Revenue Gap</p>
                    <p className="text-2xl font-bold text-amber-600">{formatCurrency((recon.net_revenue || 0) - (recon.collections?.total || 0))}</p>
                    <p className="text-xs text-[#64748B]">Net Revenue - Collections</p>
                  </div>
                  <div className="bg-white border border-[#E2E8F0] rounded-lg p-5">
                    <p className="text-xs text-[#64748B] mb-1">Collection Rate</p>
                    <p className="text-2xl font-bold text-[#0F4C81]">{recon.collection_rate}%</p>
                    <p className="text-xs text-[#64748B]">Collections / Net Revenue</p>
                  </div>
                </div>

                {/* Monthly Breakdown Table — CLICKABLE cells */}
                {recon.monthly_breakdown?.length > 0 && (
                  <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="recon-monthly-table">
                    <div className="p-4 border-b border-[#E2E8F0]">
                      <h3 className="text-base font-semibold font-['Plus_Jakarta_Sans'] text-[#0F172A]">Monthly Breakdown</h3>
                      <p className="text-xs text-[#64748B]">Each month shows only that month's confirmed SO and closed CN</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-[#F8FAFC] text-[#64748B]">
                            <th className="text-left px-4 py-3 font-medium">Month</th>
                            <th className="text-right px-4 py-3 font-medium">Invoices</th>
                            <th className="text-right px-4 py-3 font-medium">Sales Orders</th>
                            <th className="text-right px-4 py-3 font-medium">Credit Notes</th>
                            <th className="text-right px-4 py-3 font-medium">Net Revenue</th>
                            <th className="text-right px-4 py-3 font-medium">Collections</th>
                            <th className="text-right px-4 py-3 font-medium">Gap</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recon.monthly_breakdown.map((m) => {
                            const gap = (m.net_revenue || 0) - (m.collections || 0);
                            return (
                              <tr key={m.month} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC]">
                                <td className="px-4 py-3 font-medium text-[#0F172A]">{m.month}</td>
                                <td className="px-4 py-3 text-right">{formatCurrency(m.invoices || 0)}</td>
                                <td className="px-4 py-3 text-right">
                                  <span
                                    className="text-emerald-600 cursor-pointer hover:underline"
                                    onClick={() => switchTab('sales-orders')}
                                  >
                                    {formatCurrency(m.sales_orders || 0)}
                                  </span>
                                  {m.sales_orders_count > 0 && (
                                    <span className="text-[10px] text-[#64748B] ml-1">({m.sales_orders_count})</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <span
                                    className="text-red-500 cursor-pointer hover:underline"
                                    onClick={() => switchTab('credit-notes')}
                                  >
                                    {formatCurrency(m.credit_notes || 0)}
                                  </span>
                                  {m.credit_notes_count > 0 && (
                                    <span className="text-[10px] text-[#64748B] ml-1">({m.credit_notes_count})</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold">{formatCurrency(m.net_revenue || 0)}</td>
                                <td className="px-4 py-3 text-right">{formatCurrency(m.collections || 0)}</td>
                                <td className={`px-4 py-3 text-right font-medium ${gap > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                  {formatCurrency(gap)}
                                </td>
                              </tr>
                            );
                          })}
                          <tr className="bg-[#0F4C81] text-white font-semibold">
                            <td className="px-4 py-3">Total</td>
                            <td className="px-4 py-3 text-right">{formatCurrency(recon.invoices?.total || 0)}</td>
                            <td className="px-4 py-3 text-right">{formatCurrency(recon.sales_orders?.total || 0)}</td>
                            <td className="px-4 py-3 text-right">{formatCurrency(recon.credit_notes?.total || 0)}</td>
                            <td className="px-4 py-3 text-right">{formatCurrency(recon.net_revenue || 0)}</td>
                            <td className="px-4 py-3 text-right">{formatCurrency(recon.collections?.total || 0)}</td>
                            <td className="px-4 py-3 text-right">{formatCurrency((recon.net_revenue || 0) - (recon.collections?.total || 0))}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ═══ SALES ORDERS TAB ═══ */}
            {activeTab === 'sales-orders' && (
              <>
              <div className="space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
                    <p className="text-xs text-[#64748B] mb-1">All Sales Orders</p>
                    <p className="text-xl font-bold text-[#0F172A]" data-testid="so-total-amount">{formatCurrency(soSummary.total_amount || 0)}</p>
                    <p className="text-xs text-[#64748B]">{soSummary.count || 0} orders</p>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 cursor-pointer hover:border-emerald-400 transition-colors"
                    onClick={() => { setSoOrderStatus('closed'); setSoPage(1); }}
                    data-testid="so-confirmed-card"
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <CheckCircle size={14} className="text-emerald-600" />
                      <p className="text-xs text-emerald-600">Confirmed (Closed)</p>
                    </div>
                    <p className="text-xl font-bold text-emerald-700" data-testid="so-confirmed-amount">{formatCurrency(soConfirmed.total || 0)}</p>
                    <p className="text-xs text-emerald-600">{soConfirmed.count || 0} orders — Money Received</p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 cursor-pointer hover:border-amber-400 transition-colors"
                    onClick={() => { setSoOrderStatus('open'); setSoPage(1); }}
                    data-testid="so-pending-card"
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <Clock size={14} className="text-amber-600" />
                      <p className="text-xs text-amber-600">Pending (Open)</p>
                    </div>
                    <p className="text-xl font-bold text-amber-700" data-testid="so-pending-amount">{formatCurrency(soOrderStatusBd.open?.amount || 0)}</p>
                    <p className="text-xs text-amber-600">{soOrderStatusBd.open?.count || 0} orders — Not confirmed yet</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-gray-400 transition-colors"
                    onClick={() => { setSoOrderStatus('void'); setSoPage(1); }}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <Prohibit size={14} className="text-gray-500" />
                      <p className="text-xs text-gray-500">Void</p>
                    </div>
                    <p className="text-xl font-bold text-gray-600">{formatCurrency(soOrderStatusBd.void?.amount || 0)}</p>
                    <p className="text-xs text-gray-500">{soOrderStatusBd.void?.count || 0} orders</p>
                  </div>
                </div>

                {/* Filters */}
                <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[200px]">
                      <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                      <Input
                        placeholder="Search by SO#, customer, location..."
                        value={soSearch}
                        onChange={(e) => { setSoSearch(e.target.value); setSoPage(1); }}
                        className="pl-9"
                        data-testid="so-search-input"
                      />
                    </div>
                    <Select value={soOrderStatus} onValueChange={(v) => { setSoOrderStatus(v); setSoPage(1); }}>
                      <SelectTrigger className="w-44" data-testid="so-order-status-filter">
                        <Funnel size={14} className="mr-1" /><SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="closed">Confirmed (Closed)</SelectItem>
                        <SelectItem value="open">Pending (Open)</SelectItem>
                        <SelectItem value="void">Void</SelectItem>
                      </SelectContent>
                    </Select>
                    <SearchableSelect value={soLocation || 'all'} onChange={(v) => { setSoLocation(v); setSoPage(1); }}
                      options={[{ value: 'all', label: 'All Locations' }, ...soLocations.map(loc => ({ value: loc, label: loc }))]}
                      placeholder="All Locations" testid="so-location-filter" buttonClassName="w-52" />
                    <Select value={soSalesperson} onValueChange={(v) => { setSoSalesperson(v); setSoPage(1); }}>
                      <SelectTrigger className="w-44" data-testid="so-salesperson-filter">
                        <SelectValue placeholder="All Salespersons" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Salespersons</SelectItem>
                        {soSalespersons.map(sp => <SelectItem key={sp} value={sp}>{sp}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-3">
                    <div className="flex items-center gap-2">
                      <div>
                        <label className="text-[10px] uppercase tracking-wide text-[#64748B] block mb-0.5">From</label>
                        <Input type="date" value={soStartDate} onChange={(e) => { setSoStartDate(e.target.value); setSoPage(1); }} className="w-36 h-9" data-testid="so-start-date" />
                      </div>
                      <span className="text-[#64748B] mt-4">—</span>
                      <div>
                        <label className="text-[10px] uppercase tracking-wide text-[#64748B] block mb-0.5">To</label>
                        <Input type="date" value={soEndDate} onChange={(e) => { setSoEndDate(e.target.value); setSoPage(1); }} className="w-36 h-9" data-testid="so-end-date" />
                      </div>
                    </div>
                    {(soOrderStatus !== 'all' || soLocation !== 'all' || soSalesperson !== 'all' || soStartDate || soEndDate || soSearch) && (
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700"
                        data-testid="so-clear-filters"
                        onClick={() => { setSoOrderStatus('all'); setSoLocation('all'); setSoSalesperson('all'); setSoStartDate(''); setSoEndDate(''); setSoSearch(''); setSoPage(1); }}>
                        Clear Filters
                      </Button>
                    )}
                  </div>
                </div>

                {/* Table */}
                <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="so-table">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[#F8FAFC] text-[#64748B]">
                          {[
                            { key: 'so_number', label: 'SO #', align: 'left', sortable: false },
                            { key: 'customer_name', label: 'Customer', align: 'left', sortable: true },
                            { key: 'date', label: 'Date', align: 'left', sortable: true },
                            { key: 'items', label: 'Items', align: 'left', sortable: false },
                            { key: 'quantity', label: 'Qty', align: 'right', sortable: true },
                            { key: 'grand_total', label: 'Amount', align: 'right', sortable: true },
                            { key: 'location_name', label: 'Location', align: 'left', sortable: true },
                            { key: 'status', label: 'Status', align: 'left', sortable: false },
                            { key: 'salesperson_name', label: 'Salesperson', align: 'left', sortable: true },
                            { key: 'lead_source', label: 'Lead Source', align: 'left', sortable: false },
                            { key: 'actions', label: '', align: 'center', sortable: false },
                          ].map(col => (
                            <th
                              key={col.key}
                              className={`${col.align === 'right' ? 'text-right' : 'text-left'} px-4 py-3 font-medium ${col.sortable ? 'cursor-pointer hover:text-[#0F172A] select-none' : ''}`}
                              onClick={col.sortable ? () => {
                                if (soSortBy === col.key) {
                                  setSoSortDir(d => d === 'asc' ? 'desc' : 'asc');
                                } else {
                                  setSoSortBy(col.key);
                                  setSoSortDir(col.key === 'grand_total' || col.key === 'date' ? 'desc' : 'asc');
                                }
                                setSoPage(1);
                              } : undefined}
                              data-testid={`so-th-${col.key}`}
                            >
                              <span className="inline-flex items-center gap-1">
                                {col.label}
                                {col.sortable && soSortBy === col.key && (
                                  <span className="text-[10px]">{soSortDir === 'asc' ? '▲' : '▼'}</span>
                                )}
                              </span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {salesOrders.length === 0 ? (
                          <tr><td colSpan="11" className="px-4 py-8 text-center text-[#64748B]">No sales orders found</td></tr>
                        ) : salesOrders.map((so, i) => {
                          const itemsList = so.items && so.items.length > 0 ? so.items : [];
                          return (
                            <tr key={so.id || so.zoho_id || i} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC] cursor-pointer" data-testid={`so-row-${i}`} onClick={() => setSelectedSO(so)}>
                              <td className="px-4 py-3 font-medium text-[#0F4C81] whitespace-nowrap">{so.so_number || so.salesorder_number}</td>
                              <td className="px-4 py-3 text-[#0F172A] max-w-[180px]" onClick={e => e.stopPropagation()}>
                                {so.customer_name ? (
                                  <button onClick={() => setDrill({ id: so.customer_id, name: so.customer_name, phone: so.customer_phone })} className="truncate font-medium text-[#0F4C81] underline decoration-dotted underline-offset-2 hover:text-[#0F4C81] text-left block w-full" title={so.customer_name} data-testid={`sc-so-customer-${i}`}>{so.customer_name}</button>
                                ) : <div className="truncate font-medium">-</div>}
                              </td>
                              <td className="px-4 py-3 text-[#64748B] whitespace-nowrap">{so.date}</td>
                              <td className="px-4 py-3 text-[#0F172A] max-w-[200px]">
                                {itemsList.length > 0 ? (
                                  <div className="space-y-0.5">
                                    {itemsList.slice(0, 2).map((item, idx) => {
                                      const itemLabel = typeof item === 'string' ? item : (item?.name || item?.description || item?.product_code || '-');
                                      return <div key={item?.item_id || idx} className="text-xs truncate" title={itemLabel}>{itemLabel}</div>;
                                    })}
                                    {itemsList.length > 2 && (
                                      <span className="text-[10px] text-[#64748B]">+{itemsList.length - 2} more</span>
                                    )}
                                  </div>
                                ) : <span className="text-xs text-[#64748B]">-</span>}
                              </td>
                              <td className="px-4 py-3 text-right text-[#0F172A]">{so.quantity || '-'}</td>
                              <td className="px-4 py-3 text-right font-medium">{formatCurrency(so.grand_total || so.total || 0)}</td>
                              <td className="px-4 py-3 text-[#64748B] text-xs max-w-[150px]">
                                <div className="truncate" title={so.location_name}>{so.location_name || '-'}</div>
                              </td>
                              <td className="px-4 py-3"><StatusBadge status={so.order_status || so.status} type="order_status" /></td>
                              <td className="px-4 py-3 text-[#64748B] text-xs whitespace-nowrap">{so.sales_person || so.salesperson || so.salesperson_name || '-'}</td>
                              <td className="px-4 py-3 text-[#64748B] text-xs">{so.lead_source || '-'}</td>
                              <td className="px-4 py-3 text-center">
                                {!so.converted_to_invoice_id ? (
                                  (so.status === 'cancelled' || so.order_status === 'cancelled') ? (
                                    <span className="text-[10px] text-[#9CA3AF]">-</span>
                                  ) : (
                                    <Button variant="ghost" size="sm" className="h-7 text-[10px] text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2"
                                      disabled={convertingSO === so.id}
                                      onClick={(e) => { e.stopPropagation(); handleConvertSO(so); }}
                                      data-testid={`so-convert-${i}`}>
                                      <ArrowRight size={12} className="mr-0.5" /> {convertingSO === so.id ? '...' : 'Invoice'}
                                    </Button>
                                  )
                                ) : (
                                  <span className="text-[10px] text-emerald-600 font-medium" title={so.converted_to_invoice_number}>Invoiced</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-between px-4 py-3 border-t border-[#E2E8F0] bg-[#F8FAFC]">
                    <span className="text-sm text-[#64748B]">
                      Showing {salesOrders.length} of {soTotal} orders (Page {soPage}/{soTotalPages})
                    </span>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setSoPage(p => Math.max(1, p - 1))} disabled={soPage <= 1} data-testid="so-prev-page">
                        <CaretLeft size={14} />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setSoPage(p => Math.min(soTotalPages, p + 1))} disabled={soPage >= soTotalPages} data-testid="so-next-page">
                        <CaretRight size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sales Order Detail Dialog */}
              <Dialog open={!!selectedSO} onOpenChange={(open) => { if (!open) setSelectedSO(null); }}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="so-detail-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-['Plus_Jakarta_Sans']">Sales Order Details</DialogTitle>
                  </DialogHeader>
                  {selectedSO && (() => {
                    const liArr = selectedSO.line_items || [];
                    const totalQty = liArr.reduce((s, li) => s + (li.quantity_ordered ?? li.quantity ?? 0), 0);
                    const totalInv = liArr.reduce((s, li) => s + (li.quantity_invoiced ?? 0), 0);
                    const totalPak = liArr.reduce((s, li) => s + (li.quantity_packed ?? 0), 0);
                    const invStatus = totalInv >= totalQty && totalQty > 0 ? 'invoiced' : (totalInv > 0 ? 'partially invoiced' : 'not invoiced');
                    const salesPerson = selectedSO.sales_person || selectedSO.salesperson || selectedSO.salesperson_name;
                    return (
                    <div className="space-y-5">
                      {/* Header Info */}
                      <div className="flex items-start justify-between">
                        <div>
                          <h2 className="text-xl font-bold text-[#0F172A]">{selectedSO.so_number || selectedSO.salesorder_number}</h2>
                          <p className="text-sm text-[#64748B]">Date: {selectedSO.date}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {!selectedSO.converted_to_invoice_id && selectedSO.status !== 'cancelled' && selectedSO.order_status !== 'cancelled' && (
                            <Button size="sm" className="h-8 bg-[#0F4C81] text-white hover:bg-[#0B3C68]"
                              disabled={convertingSO === selectedSO.id}
                              onClick={() => handleConvertSO(selectedSO)}
                              data-testid="so-detail-convert-btn">
                              <ArrowRight size={14} className="mr-1" /> {convertingSO === selectedSO.id ? 'Converting...' : 'Convert to Invoice'}
                            </Button>
                          )}
                          {selectedSO.converted_to_invoice_id && (
                            <span className="text-xs text-emerald-600 font-medium">Invoiced: {selectedSO.converted_to_invoice_number}</span>
                          )}
                          <StatusBadge status={selectedSO.order_status || selectedSO.status} type="order_status" />
                        </div>
                      </div>

                      {/* Key Details Grid */}
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="bg-[#F8FAFC] rounded-lg p-3">
                          <p className="text-xs text-[#64748B]">Customer</p>
                          <p className="font-semibold text-[#0F172A]">{selectedSO.customer_name || '-'}</p>
                        </div>
                        <div className="bg-[#F8FAFC] rounded-lg p-3">
                          <p className="text-xs text-[#64748B]">Location</p>
                          <p className="font-semibold text-[#0F172A]">{selectedSO.location_name || selectedSO.clinic_name || '-'}</p>
                        </div>
                        <div className="bg-[#F8FAFC] rounded-lg p-3">
                          <p className="text-xs text-[#64748B]">Salesperson</p>
                          <p className="font-semibold text-[#0F172A]" data-testid="so-detail-salesperson">{salesPerson || '-'}</p>
                        </div>
                        <div className="bg-[#F8FAFC] rounded-lg p-3">
                          <p className="text-xs text-[#64748B]">Lead Source</p>
                          <p className="font-semibold text-[#0F172A]">{selectedSO.lead_source || selectedSO.source || '-'}</p>
                        </div>
                        <div className="bg-[#F8FAFC] rounded-lg p-3">
                          <p className="text-xs text-[#64748B]">Quantity</p>
                          <p className="font-semibold text-[#0F172A]" data-testid="so-detail-quantity">{totalQty || selectedSO.quantity || '-'}</p>
                        </div>
                        <div className="bg-[#F8FAFC] rounded-lg p-3">
                          <p className="text-xs text-[#64748B]">Invoiced Status</p>
                          <p className="font-semibold text-[#0F172A] capitalize" data-testid="so-detail-invoiced-status">
                            {invStatus}
                            {totalQty > 0 && <span className="text-[10px] text-[#64748B] ml-1 font-normal">({totalInv}/{totalQty} invoiced{totalPak > 0 ? `, ${totalPak} packed` : ''})</span>}
                          </p>
                        </div>
                      </div>

                      {/* Line Items — full columns matching CN/Invoice */}
                      {liArr.length > 0 && (
                        <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="so-line-items">
                          <div className="p-3 bg-[#F8FAFC] border-b border-[#E2E8F0]">
                            <h3 className="text-sm font-medium text-[#0F172A]">Line Items ({liArr.length})</h3>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-white text-[#64748B] border-b border-[#E2E8F0]">
                                  <th className="text-left p-2 font-medium">Item</th>
                                  <th className="text-left p-2 font-medium">HSN</th>
                                  <th className="text-right p-2 font-medium">Qty</th>
                                  <th className="text-right p-2 font-medium">Rate</th>
                                  <th className="text-right p-2 font-medium">Disc</th>
                                  <th className="text-right p-2 font-medium">Tax%</th>
                                  <th className="text-right p-2 font-medium">Amount</th>
                                  <th className="text-left p-2 font-medium">Warehouse</th>
                                  <th className="text-left p-2 font-medium">Serials</th>
                                </tr>
                              </thead>
                              <tbody>
                                {liArr.map((li, idx) => (
                                  <tr key={idx} className="border-t border-[#E2E8F0]">
                                    <td className="p-2 font-medium">{li.name || '-'}</td>
                                    <td className="p-2 text-[#64748B] font-mono">{li.hsn_sac || li.hsn || '-'}</td>
                                    <td className="p-2 text-right">{li.quantity ?? li.quantity_ordered ?? '-'}</td>
                                    <td className="p-2 text-right">{formatCurrency(li.rate || 0)}</td>
                                    <td className="p-2 text-right text-[#64748B]">{li.discount_amount || li.discount ? formatCurrency(li.discount_amount || li.discount) : '-'}</td>
                                    <td className="p-2 text-right text-[#64748B]">{li.tax_percent ? `${li.tax_percent}%` : '-'}</td>
                                    <td className="p-2 text-right font-medium">{formatCurrency(li.amount || li.line_amount || li.item_total || li.total || 0)}</td>
                                    <td className="p-2 text-[#64748B] text-[10px]">{li.line_item_location_name || li.warehouse_name || '-'}</td>
                                    <td className="p-2">{(li.serial_numbers || []).length > 0 ? (li.serial_numbers || []).map(sn => (
                                      <span key={sn} className="text-[10px] bg-emerald-50 text-emerald-700 px-1 py-0.5 rounded font-mono mr-1">{sn}</span>
                                    )) : <span className="text-[#64748B]">-</span>}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Totals */}
                      <div className="bg-[#0F4C81] rounded-lg p-4 space-y-1">
                        <div className="flex justify-between text-sm text-emerald-200"><span>Total Quantity</span><span>{totalQty || '-'}</span></div>
                        {selectedSO.subtotal && <div className="flex justify-between text-sm text-emerald-200"><span>Subtotal</span><span>{formatCurrency(selectedSO.subtotal)}</span></div>}
                        {(selectedSO.total_tax || selectedSO.tax_total) ? <div className="flex justify-between text-sm text-emerald-200"><span>Tax</span><span>{formatCurrency(selectedSO.total_tax || selectedSO.tax_total)}</span></div> : null}
                        {selectedSO.adjustment ? <div className="flex justify-between text-sm text-emerald-200"><span>Adjustment</span><span>{formatCurrency(selectedSO.adjustment)}</span></div> : null}
                        <div className="flex justify-between text-lg font-bold text-white border-t border-emerald-700 mt-1 pt-2">
                          <span>Grand Total</span><span>{formatCurrency(selectedSO.grand_total || selectedSO.total || 0)}</span>
                        </div>
                      </div>

                      {/* Linked Invoices + serials allocated */}
                      {selectedSODetail?.linked_invoices?.length > 0 && (
                        <div className="border border-blue-200 bg-blue-50 rounded-lg p-3" data-testid="so-linked-invoices-salescredits">
                          <p className="text-xs font-medium text-blue-800 mb-2">
                            Linked Invoices ({selectedSODetail.linked_invoices.length}) — serials allocated when this SO was invoiced
                          </p>
                          <div className="space-y-1">
                            {selectedSODetail.linked_invoices.map((inv, i) => (
                              <div key={i} className="flex items-center justify-between text-xs bg-white border border-blue-100 rounded px-2 py-1">
                                <span className="font-mono text-[#0F4C81]">{inv.invoice_number}</span>
                                <span className="text-[#64748B]">{inv.date}</span>
                                <span className="font-medium">{formatCurrency(inv.grand_total || 0)}</span>
                                <span className="text-blue-700 text-[10px]">{inv.serial_count} serial{inv.serial_count === 1 ? '' : 's'}</span>
                              </div>
                            ))}
                          </div>
                          {selectedSODetail.linked_invoice_serials?.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-blue-200">
                              <p className="text-[10px] uppercase tracking-wide text-blue-700 mb-1">All Serials Allocated</p>
                              <div className="flex flex-wrap gap-1">
                                {selectedSODetail.linked_invoice_serials.map((s, i) => (
                                  <span key={i} className="text-[10px] bg-white border border-blue-200 rounded px-1.5 py-0.5">
                                    <span className="font-mono text-blue-800">{s.serial_number}</span>
                                    <span className="text-[#64748B]"> — {s.item_name}</span>
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Convert to Invoice Action */}
                      {!selectedSO.converted_to_invoice_id && (selectedSO.order_status === 'closed' || selectedSO.status === 'confirmed' || selectedSO.status === 'sent') && (
                        <Button className="w-full bg-blue-600 text-white hover:bg-blue-700"
                          onClick={() => { setSelectedSO(null); navigate(`/create-invoice?so_id=${selectedSO.id}`); }}
                          data-testid="so-dialog-convert-btn">
                          <ArrowRight size={16} className="mr-2" /> Convert to Tax Invoice
                        </Button>
                      )}
                    </div>
                    );
                  })()}
                </DialogContent>
              </Dialog>
              </>
            )}
            {activeTab === 'credit-notes' && (
              <div className="space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
                    <p className="text-xs text-[#64748B] mb-1">Total Credit Notes</p>
                    <p className="text-xl font-bold text-red-600" data-testid="cn-total-amount">{formatCurrency(cnSummary.total_amount || 0)}</p>
                    <p className="text-xs text-[#64748B]">{cnSummary.count || 0} notes</p>
                  </div>
                  {Object.entries(cnStatusBd).map(([status, data]) => (
                    <div key={status}
                      className="bg-white border border-[#E2E8F0] rounded-lg p-4 cursor-pointer hover:border-[#0F4C81] transition-colors"
                      onClick={() => { setCnStatus(status); setCnPage(1); }}
                    >
                      <p className="text-xs text-[#64748B] mb-1 capitalize">{status}</p>
                      <p className="text-xl font-bold text-[#0F172A]">{formatCurrency(data.amount || 0)}</p>
                      <p className="text-xs text-[#64748B]">{data.count} notes</p>
                    </div>
                  ))}
                </div>

                {/* Filters */}
                <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[200px]">
                      <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                      <Input
                        placeholder="Search by CN# or customer..."
                        value={cnSearch}
                        onChange={(e) => { setCnSearch(e.target.value); setCnPage(1); }}
                        className="pl-9"
                        data-testid="cn-search-input"
                      />
                    </div>
                    <Select value={cnStatus} onValueChange={(v) => { setCnStatus(v); setCnPage(1); }}>
                      <SelectTrigger className="w-44" data-testid="cn-status-filter">
                        <Funnel size={14} className="mr-1" /><SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="open">Open</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="h-6 w-px bg-[#E2E8F0]" />
                    <div className="flex items-center gap-2">
                      <div>
                        <label className="text-[10px] uppercase tracking-wide text-[#64748B] block mb-0.5">From</label>
                        <Input type="date" value={cnStartDate} onChange={(e) => { setCnStartDate(e.target.value); setCnPage(1); }} className="w-36 h-9" data-testid="cn-start-date" />
                      </div>
                      <span className="text-[#64748B] mt-4">—</span>
                      <div>
                        <label className="text-[10px] uppercase tracking-wide text-[#64748B] block mb-0.5">To</label>
                        <Input type="date" value={cnEndDate} onChange={(e) => { setCnEndDate(e.target.value); setCnPage(1); }} className="w-36 h-9" data-testid="cn-end-date" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table */}
                <CreditNoteDetailDialog cn={selectedCN} open={!!selectedCN} onClose={() => setSelectedCN(null)} />
                <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="cn-table">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[#F8FAFC] text-[#64748B]">
                          <th className="text-left px-4 py-3 font-medium">CN #</th>
                          <th className="text-left px-4 py-3 font-medium">Customer</th>
                          <th className="text-left px-4 py-3 font-medium">Date</th>
                          <th className="text-right px-4 py-3 font-medium">Amount</th>
                          <th className="text-right px-4 py-3 font-medium">Balance</th>
                          <th className="text-left px-4 py-3 font-medium">Status</th>
                          <th className="text-left px-4 py-3 font-medium">Reference</th>
                        </tr>
                      </thead>
                      <tbody>
                        {creditNotes.length === 0 ? (
                          <tr><td colSpan="7" className="px-4 py-8 text-center text-[#64748B]">No credit notes found</td></tr>
                        ) : creditNotes.map((cn, i) => (
                          <tr key={cn.zoho_id || i} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC] cursor-pointer" data-testid={`cn-row-${i}`} onClick={() => setSelectedCN(cn)}>
                            <td className="px-4 py-3 font-medium text-[#0F4C81]">{cn.creditnote_number || cn.cn_number}</td>
                            <td className="px-4 py-3 text-[#0F172A] max-w-[200px] truncate" onClick={e => e.stopPropagation()}>
                              {cn.customer_name ? (
                                <button onClick={() => setDrill({ id: cn.customer_id, name: cn.customer_name })} className="text-[#0F4C81] underline decoration-dotted underline-offset-2 hover:text-[#0F4C81] text-left truncate block w-full" title={cn.customer_name} data-testid={`sc-cn-customer-${i}`}>{cn.customer_name}</button>
                              ) : '-'}
                            </td>
                            <td className="px-4 py-3 text-[#64748B]">{cn.date}</td>
                            <td className="px-4 py-3 text-right font-medium text-red-600">{formatCurrency(cn.total || cn.grand_total || 0)}</td>
                            <td className="px-4 py-3 text-right">{formatCurrency(cn.balance || 0)}</td>
                            <td className="px-4 py-3"><StatusBadge status={cn.status} /></td>
                            <td className="px-4 py-3 text-[#64748B] text-xs font-mono">{cn.reference_invoice_number || cn.reference_number || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-between px-4 py-3 border-t border-[#E2E8F0] bg-[#F8FAFC]">
                    <span className="text-sm text-[#64748B]">
                      Showing {creditNotes.length} of {cnTotal} notes (Page {cnPage}/{cnTotalPages})
                    </span>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setCnPage(p => Math.max(1, p - 1))} disabled={cnPage <= 1} data-testid="cn-prev-page">
                        <CaretLeft size={14} />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setCnPage(p => Math.min(cnTotalPages, p + 1))} disabled={cnPage >= cnTotalPages} data-testid="cn-next-page">
                        <CaretRight size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <CustomerDrillDownDialog open={!!drill} onOpenChange={o => { if (!o) setDrill(null); }} customerId={drill?.id || ''} customerName={drill?.name || ''} customerPhone={drill?.phone || ''} />
    </div>
  );
}
